// Hands the workup's biometry to an external IOL power calculator, through BiomAPI.
//
// BiomAPI (https://biomapi.com/docs/) stores a biometry report under a short-lived encrypted code -
// a "BiomPIN" - and hands a calculator a link that imports it. Values supplied by an EHR rather than
// read off a printout are BiomAPI's "BiomDIRECT" path: we build the report from what the user loaded
// or typed into the surgical plan, POST it to /api/v1/biom/process with biompin=true, and put the
// returned PIN on the calculator's URL.
//
// What identifies the patient is held to the two things the calculator needs to run and label a
// calculation: their initials and their gender. No full name, no NHI, no date of birth, no CatTrax
// patient id. Initials are formed here rather than sent for BiomAPI to shorten, and travel in the
// link's fragment rather than in the stored share. Session.token is CatTrax's own credential and is
// never sent to BiomAPI. See .claude/rules/data-safety.md.
app.service("BiomAPIService", [
	"$http",
	"$q",
	"$window",
	"ENV",
	function ($http, $q, $window, ENV) {
		// Our biometry scan types (BiometryType on the backend) to BiomAPI's device enum. A scan we
		// cannot place, and anything typed in by hand, is reported honestly as "Other".
		const DEVICES = {
			MASTER_IOL: { device_name: "IOLMaster700", manufacturer: "Zeiss" },
			ANTERION: { device_name: "Anterion", manufacturer: "Heidelberg" },
			ARGOS: { device_name: "Argos", manufacturer: "Alcon" },
			TOPCON: { device_name: "Aladdin", manufacturer: "Topcon" },
			LENSTAR: { device_name: "LenstarLS900", manufacturer: "HaagStreit" },
			EYESTAR: { device_name: "EyestarES900", manufacturer: "HaagStreit" },
			// OPTOPOL (Optopol REVO) is deliberately absent: BiomAPI's device enum has no Optopol
			// entry, so an OPTOPOL scan is handed over as UNKNOWN_DEVICE ("Other") below rather than
			// mislabelled with a device_name BiomAPI would reject.
		};
		const UNKNOWN_DEVICE = { device_name: "Other", manufacturer: "Other" };

		// LENS_STATUS in constants.js to BiomAPI's enum. BiomAPI has no separate value for PCO, which
		// is a finding about a pseudophakic eye rather than a different lens state.
		const LENS_STATUS = {
			PHAKIC: "Phakic",
			PSEUDO_PHAKIC: "Pseudophakic",
			PSEUDO_PHAKIC_WITH_PCO: "Pseudophakic",
			APHAKIC: "Aphakic",
		};

		// BiomAPI's accepted input ranges. A value outside one of these fails validation for the whole
		// report, so an implausible reading is dropped rather than allowed to sink the hand-off.
		const RANGES = {
			AL: [14, 40],
			ACD: [1, 6],
			K: [20, 99],
			WTW: [7, 17],
			LT: [0.1, 7],
			CCT: [200, 900],
		};

		// BiomAPI's gender enum. "Other", which CatTrax records and BiomAPI has no value for, is left
		// out rather than guessed at - the calculator can ask for it.
		const GENDERS = { MALE: "Male", FEMALE: "Female" };

		function firstOf(preferred, fallback) {
			return preferred === null || preferred === undefined || preferred === "" ? fallback : preferred;
		}

		function measurement(value, range) {
			const number = parseFloat(value);
			if (!isFinite(number) || number < range[0] || number > range[1]) {
				return null;
			}
			return number;
		}

		function axis(value) {
			const degrees = Math.round(parseFloat(value));
			if (!isFinite(degrees) || degrees < 0 || degrees > 180) {
				return null;
			}
			return degrees;
		}

		// The meridian 90 degrees from the one given, kept inside BiomAPI's 0-180 range. This is the
		// same rotation preOpPlanBiometryController uses to derive the steep axis for its summary.
		function perpendicular(degrees) {
			return degrees > 90 ? degrees - 90 : degrees + 90;
		}

		// One eye of the report. Returns {} for an eye with nothing usable, which is what BiomAPI wants
		// for an eye it has no data on - the objects themselves are required, their contents are not.
		//
		// `imported` is the eye record off the biometry scan the workup was loaded from, when there was
		// one. CCT, LT and WTW are read from it because the workup itself cannot keep them: the
		// SurgicalPlanBiometry schema has no field for any of the three, so they survive a Load Biometry
		// only until the plan is saved. AL, ACD and K always come from the form, which is where the
		// clinician's own corrections are.
		function buildEye(biometry, imported, lensStatus) {
			const eye = {};
			if (!biometry) {
				return eye;
			}
			const scan = imported || {};

			const al = measurement(biometry.al, RANGES.AL);
			const acd = measurement(biometry.acd, RANGES.ACD);
			const wtw = measurement(firstOf(biometry.wtw, scan.wtw), RANGES.WTW);
			const lt = measurement(firstOf(biometry.lens_thickness, scan.lens_thickness), RANGES.LT);
			const cct = measurement(firstOf(biometry.cct, scan.cct), RANGES.CCT);
			if (al !== null) eye.AL = al;
			if (acd !== null) eye.ACD = acd;
			if (wtw !== null) eye.WTW = wtw;
			if (lt !== null) eye.LT = lt;
			if (cct !== null) eye.CCT = cct;

			// BiomAPI reads K1 as the flat meridian and rejects a report where K1 > K2. The biometer's
			// own labelling does not guarantee that ordering - an Anterion export routinely carries the
			// steeper reading as K1 - so sort the pair here and keep each axis with its own reading.
			//
			// Only k1_axis is used. It is the axis the form collects, and an imported scan's k2_axis is
			// redundant with it; BiomAPI requires the two axes to be exactly 90 degrees apart, so a scan
			// that rounded them independently would fail validation for a difference of one degree.
			const k1 = measurement(biometry.k1, RANGES.K);
			const k2 = measurement(biometry.k2, RANGES.K);
			const k1Axis = axis(biometry.k1_axis);
			const steepIsFirst = k1 !== null && k2 !== null && k1 > k2;
			const flat = steepIsFirst ? k2 : k1;
			const steep = steepIsFirst ? k1 : k2;

			if (flat !== null) {
				eye.K1_magnitude = flat;
				if (k1Axis !== null) {
					eye.K1_axis = steepIsFirst ? perpendicular(k1Axis) : k1Axis;
				}
			}
			if (steep !== null) {
				eye.K2_magnitude = steep;
				if (k1Axis !== null) {
					eye.K2_axis = steepIsFirst ? k1Axis : perpendicular(k1Axis);
				}
			}

			if (LENS_STATUS[lensStatus]) {
				eye.lens_status = LENS_STATUS[lensStatus];
			}
			return eye;
		}

		const service = {
			// Whether there is a calculator to open at all. Enough on its own to offer the button,
			// because opening the calculator empty is still useful - the surgeon can work in it.
			isConfigured: function () {
				return !!ENV.BIOMAPI_IOL_CALCULATOR_URL;
			},

			// Whether measurements can be handed over as well as the calculator opened. Needs the
			// BiomAPI host and the SDK script from index.html, which is third-party and can be blocked.
			isAvailable: function () {
				return !!(
					service.isConfigured() &&
					ENV.BIOMAPI_URL &&
					$window.BiomAPI &&
					$window.BiomAPI.context
				);
			},

			// Whether an eye holds enough for a power calculation to mean anything. Axial length and
			// both keratometry readings are the minimum any of the formulas need; ACD alone, which is
			// all the workup insists on before it will save a biometry block, is not a calculation.
			hasCalculableBiometry: function (biometry) {
				if (!biometry) {
					return false;
				}
				return (
					measurement(biometry.al, RANGES.AL) !== null &&
					measurement(biometry.k1, RANGES.K) !== null &&
					measurement(biometry.k2, RANGES.K) !== null
				);
			},

			// Initials for BiomAPI's patient name, which the calculator needs to label the calculation.
			// Formed here so the full name never leaves the browser: BiomAPI would reduce a name it was
			// given to initials anyway, but only after receiving it.
			initials: function (firstName, lastName) {
				const letters = [firstName, lastName]
					.map(function (part) {
						return typeof part === "string" ? part.trim().charAt(0) : "";
					})
					.join("")
					.toUpperCase();
				return letters || null;
			},

			// BiomAPI's patient id: the NHI and/or the Medicare number (with its IRN), whichever the
			// patient has, so the calculation can be matched back to them. Null when they have neither.
			patientId: function (nhi, medicareDetails) {
				const medicare = medicareDetails || {};
				const cardNumber = typeof medicare.card_number === "string" ? medicare.card_number.trim() : medicare.card_number;
				const ids = [];
				if (typeof nhi === "string" && nhi.trim()) {
					ids.push(`NHI ${nhi.trim().toUpperCase()}`);
				}
				if (cardNumber) {
					ids.push(medicare.irn ? `Medicare ${cardNumber}-${medicare.irn}` : `Medicare ${cardNumber}`);
				}
				return ids.length ? ids.join(" / ") : null;
			},

			// A BiomDIRECT report. `eyes` is { od, os }, each side either null or
			// { biometry, imported, lensStatus }; a null side is sent as the empty object BiomAPI wants
			// for an eye it has no data on. The caller decides which sides to fill, so a one-eye plan
			// hands over one eye and a both-eyes plan hands over both.
			buildReport: function (eyes, options) {
				const settings = options || {};
				const sides = eyes || {};
				function measurementsFor(side) {
					const eye = sides[side];
					return eye ? buildEye(eye.biometry, eye.imported, eye.lensStatus) : {};
				}
				// Initials, gender and the NHI/Medicare id - no full name, no date of birth. BiomPIN
				// storage drops name and id before writing the record, so those two reach the
				// calculator through the link's #biomctx= fragment rather than through the share.
				const patient = {};
				if (settings.initials) {
					patient.name = settings.initials;
				}
				if (settings.patientId) {
					patient.id = settings.patientId;
				}
				if (GENDERS[settings.gender]) {
					patient.gender = GENDERS[settings.gender];
				}

				return {
					data: {
						biometer: DEVICES[settings.deviceType] || UNKNOWN_DEVICE,
						patient: patient,
						right_eye: measurementsFor("od"),
						left_eye: measurementsFor("os"),
					},
					metadata: {
						extraction: {
							method: "BiomDIRECT",
							source_app: ENV.BIOMAPI_INTEGRATOR_NAME,
						},
					},
				};
			},

			// Validates the report and stores it behind a new BiomPIN. BiomAPI routes the upload by file
			// extension, so the JSON goes up as a .json file rather than as a request body - the .json
			// extension selects its schema-validation engine, which never calls out to an LLM.
			createShare: function (report) {
				const form = new FormData();
				form.append("file", new Blob([angular.toJson(report)], { type: "application/json" }), "biometry.json");
				form.append("biompin", "true");

				return $http({
					method: "POST",
					url: ENV.BIOMAPI_URL + "/api/v1/biom/process",
					headers: {
						// Let the browser set the multipart boundary.
						"Content-Type": undefined,
						// Attribution only - BiomAPI's public access needs no credential, and no CatTrax
						// token or cookie may be handed to a third-party origin.
						"X-BiomAPI-Integrator-ID": ENV.BIOMAPI_INTEGRATOR_ID,
					},
					data: form,
					transformRequest: angular.identity,
					// The app sets withCredentials globally for its own API. Cross-origin cookies have no
					// business at BiomAPI, and sending them would fail its CORS check anyway.
					withCredentials: false,
				});
			},

			// The calculator link for a PIN.
			//
			// The PIN goes in the *query string*, which is how the ESCRS calculator reads it and what
			// BiomAPI's own web app builds (`url.searchParams.set('biompin', pin)`). The browser SDK's
			// biompin.buildUrl() is not used here: it emits the integrator format, "<target>#biompin=",
			// which only an app calling BiomAPI.fragments.consume() will see. The calculator does not,
			// so a fragment link opens it with nothing imported.
			//
			// Initials and the patient id ride along in a #biomctx= fragment, because BiomPIN storage
			// strips name and id from the record itself. A fragment is not sent to any server, but it is
			// reversible, so a complete link is patient-identifying - it goes to the calculator, and
			// nowhere else. Without a pin this is just the calculator's own address, for opening it empty.
			calculatorUrl: function (pin, initials, patientId) {
				const url = new $window.URL(ENV.BIOMAPI_IOL_CALCULATOR_URL);
				if (!pin) {
					return url.toString();
				}
				url.searchParams.set("biompin", pin);
				const context =
					initials || patientId
						? $window.BiomAPI.context.encode({ patientName: initials || null, patientId: patientId || null })
						: null;
				return context ? `${url.toString()}#biomctx=${context}` : url.toString();
			},

			// The whole hand-off: report, share, link. Resolves with the calculator URL.
			buildCalculatorLink: function (eyes, options) {
				if (!service.isConfigured()) {
					return $q.reject(new Error("The IOL calculator is not configured."));
				}
				const settings = options || {};
				// Nothing worth handing over, or no way to hand it over: open the calculator as it
				// comes. Creating a share for an eye with no measurements would spend a BiomAPI credit
				// and leave an empty encrypted record behind for 31 days, and the calculator would open
				// on it no better off than if we had sent nothing.
				if (!service.isAvailable() || !Object.keys(eyes || {}).length) {
					return $q.when(service.calculatorUrl(null, null));
				}
				return service.createShare(service.buildReport(eyes, settings)).then(function (resp) {
					// Processing can succeed while sharing is unavailable, in which case there is no PIN
					// and so nothing for the calculator to import.
					const pin = resp.data && resp.data.biompin && resp.data.biompin.pin;
					if (!pin) {
						return $q.reject(new Error("BiomAPI accepted the measurements but did not return a share code."));
					}
					return service.calculatorUrl(pin, settings.initials, settings.patientId);
				});
			},

			// What to show the user when a hand-off fails. Status codes and BiomAPI's own message only -
			// never the request body, which is biometry.
			failureMessage: function (rejection) {
				if (rejection instanceof Error) {
					return rejection.message;
				}
				const status = rejection && rejection.status;
				const error = rejection && rejection.data && rejection.data.error;
				if (status === 429) {
					return "BiomAPI is rate limiting this practice. Try again shortly.";
				}
				if (status === 422 || status === 400) {
					return "BiomAPI would not accept these measurements. Check the biometry values.";
				}
				if (!status || status <= 0) {
					return "Could not reach BiomAPI. Check your connection and try again.";
				}
				return (error && error.message) || `BiomAPI returned an error (HTTP ${status}).`;
			},
		};

		return service;
	},
]);
